import { useEffect, useMemo, useState } from "react";
import { formatUzPhone } from "../phone";
import { api, fmtDateTime, parseServerDate, type BotLead, type DriverFunnel } from "../api";
import { BarTrend } from "../components/BarTrend";
import { downloadCsv } from "../util/csv";

const PERIODS: { months: number; label: string }[] = [
  { months: 0, label: "Barchasi" },
  { months: 1, label: "So'nggi 1 oy" },
  { months: 3, label: "So'nggi 3 oy" },
  { months: 6, label: "So'nggi 6 oy" },
];

const TG_COLOR = "var(--brand)";
const PHONE_COLOR = "var(--muted)";

function withinMonths(created_at: string, months: number): boolean {
  if (!months) return true;
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);
  return parseServerDate(created_at) >= cutoff;
}

const pad = (n: number) => String(n).padStart(2, "0");

type Bucket = { label: string; count: number };

// Vaqt filtridan o'tgan qatorlarni grafik uchun guruhlaydi: 1/3 oy uchun
// kunlik, 6 oy va "Barchasi" uchun oylik ustunlar.
function bucketByPeriod(rows: BotLead[], months: number): Bucket[] {
  if (rows.length === 0) return [];
  const now = new Date();
  const byMonth = months === 0 || months >= 6;

  let start: Date;
  if (months > 0) {
    start = new Date(now);
    start.setMonth(start.getMonth() - months);
  } else {
    start = rows.reduce((min, r) => {
      const d = parseServerDate(r.created_at);
      return d < min ? d : min;
    }, now);
  }

  const counts = new Map<string, number>();
  const keys: string[] = [];

  if (byMonth) {
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 1);
    while (cur <= end) {
      const k = `${cur.getFullYear()}-${pad(cur.getMonth() + 1)}`;
      counts.set(k, 0);
      keys.push(k);
      cur.setMonth(cur.getMonth() + 1);
    }
    for (const r of rows) {
      const d = parseServerDate(r.created_at);
      const k = `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
      if (counts.has(k)) counts.set(k, (counts.get(k) ?? 0) + 1);
    }
  } else {
    const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    while (cur <= end) {
      const k = `${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}`;
      counts.set(k, 0);
      keys.push(k);
      cur.setDate(cur.getDate() + 1);
    }
    for (const r of rows) {
      const d = parseServerDate(r.created_at);
      const k = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      if (counts.has(k)) counts.set(k, (counts.get(k) ?? 0) + 1);
    }
  }

  return keys.map((k) => ({ label: k, count: counts.get(k) ?? 0 }));
}

function DriverStage({ label, count, pct, top }: { label: string; count: number; pct: number | null; top?: number }) {
  const width = top ? Math.max(2, (count / Math.max(1, top)) * 100) : 100;
  return (
    <div className="funnel-stage">
      <div className="bar-wrap">
        <div className="bar-fill" style={{ width: `${width}%` }} />
        <div className="bar-label">{label}</div>
      </div>
      <div className="stage-meta">
        <strong>{count}</strong>
        {pct !== null && <span className="stage-pct"> · {Math.round(pct * 100)}%</span>}
      </div>
    </div>
  );
}

export function Haydovchilar() {
  const [rows, setRows] = useState<BotLead[] | null>(null);
  const [df, setDf] = useState<DriverFunnel | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [months, setMonths] = useState(0);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [r, funnel] = await Promise.all([
          api.botHaydovchilar(),
          api.driverFunnel(),
        ]);
        setRows(r);
        setDf(funnel);
      } catch (e) { setErr((e as Error).message); }
    })();
  }, []);

  // Davr bo'yicha filtr — KPI/grafik shu to'plamdan hisoblanadi.
  const filtered = useMemo(
    () => (rows ?? []).filter((l) => withinMonths(l.created_at, months)),
    [rows, months],
  );

  const tgLinked = useMemo(
    () => filtered.filter((l) => l.telegram_id != null).length,
    [filtered],
  );
  const tgPct = filtered.length ? Math.round((tgLinked / filtered.length) * 100) : 0;

  const buckets = useMemo(() => bucketByPeriod(filtered, months), [filtered, months]);

  const tableRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter(
      (l) =>
        l.ism.toLowerCase().includes(q) ||
        (l.telefon ?? "").toLowerCase().includes(q),
    );
  }, [filtered, query]);

  function exportCsv() {
    const header = ["Ism", "Telefon", "Telegram", "Sana"];
    const data = tableRows.map((l) => [
      l.ism,
      l.telefon,
      l.telegram_id != null ? "Ha" : "Yo'q",
      fmtDateTime(l.created_at),
    ]);
    downloadCsv(`haydovchilar-${new Date().toISOString().slice(0, 10)}.csv`, [header, ...data]);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Haydovchilar</h1>
          <p className="page-sub">Bot orqali haydovchi bo'lib qiziqish bildirganlar</p>
        </div>
        <div className="actions">
          <button className="btn ghost" onClick={exportCsv} disabled={!rows || tableRows.length === 0}>
            CSV eksport
          </button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {/* Haydovchi faollashuv voronkasi — ilova garaji bo'yicha (bot lead emas) */}
      <div className="panel">
        <div className="panel-title">
          <h2>Faollashuv voronkasi</h2>
          <span className="muted">Ro'yxat → Mashina → Faol</span>
        </div>
        {df ? (
          <div className="funnel">
            <DriverStage label="Ro'yxatdan o'tgan" count={df.registered} pct={null} />
            <DriverStage label="Mashinasi bor" count={df.has_car} pct={df.has_car_pct} top={df.registered} />
            <DriverStage label="Faol (xarajat kiritgan)" count={df.active} pct={df.active_pct} top={df.registered} />
          </div>
        ) : (
          <div className="skel" style={{ height: 120 }} />
        )}
      </div>

      <div className="chips" style={{ marginBottom: 16 }}>
        {PERIODS.map((p) => (
          <button
            key={p.months}
            className={months === p.months ? "active" : ""}
            onClick={() => setMonths(p.months)}
          >{p.label}</button>
        ))}
      </div>

      <div className="haydovchilar-grid two-col-grid">
        <div className="panel">
          <div className="panel-title">
            <h2>Ro'yxatdan o'tish dinamikasi</h2>
            <span className="muted">{PERIODS.find((p) => p.months === months)?.label}</span>
          </div>
          {!rows ? (
            <div className="skel" style={{ height: 160 }} />
          ) : (
            <BarTrend buckets={buckets} emptyText="Ma'lumot yo'q" />
          )}
        </div>

        <div className="panel">
          <div className="panel-title"><h2>Telegram ulanishi</h2></div>
          {rows && filtered.length > 0 ? (
            <>
              <div className="dist-bar">
                {tgLinked > 0 && (
                  <div className="seg" style={{ flex: tgLinked, background: TG_COLOR }} title={`Telegram ulangan: ${tgLinked}`} />
                )}
                {filtered.length - tgLinked > 0 && (
                  <div className="seg" style={{ flex: filtered.length - tgLinked, background: PHONE_COLOR }} title={`Faqat telefon: ${filtered.length - tgLinked}`} />
                )}
              </div>
              <div className="dist-legend">
                <div>
                  <span className="dot" style={{ background: TG_COLOR }} />
                  Telegram ulangan: <strong>{tgLinked}</strong>
                  <span className="muted"> ({tgPct}%)</span>
                </div>
                <div>
                  <span className="dot" style={{ background: PHONE_COLOR }} />
                  Faqat telefon: <strong>{filtered.length - tgLinked}</strong>
                  <span className="muted"> ({100 - tgPct}%)</span>
                </div>
              </div>
            </>
          ) : (
            <div className="empty">Ma'lumot yo'q</div>
          )}
        </div>
      </div>

      <div className="filters">
        <label className="grow">
          Qidiruv
          <input
            type="text"
            placeholder="Ism yoki telefon bo'yicha qidirish…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>

      <div className="panel flush">
        {!rows ? (
          <div className="empty">Yuklanmoqda…</div>
        ) : tableRows.length === 0 ? (
          <div className="empty">Ma'lumot yo'q</div>
        ) : (
          <table className="table">
            <thead><tr>
              <th>Ism</th>
              <th>Telefon</th>
              <th>Telegram</th>
              <th>Sana</th>
            </tr></thead>
            <tbody>
              {tableRows.map((l) => (
                <tr key={l.id}>
                  <td><strong>{l.ism || "—"}</strong></td>
                  <td>{formatUzPhone(l.telefon) || "—"}</td>
                  <td>{l.telegram_id != null ? <span className="badge blue">Telegram</span> : <span className="muted">—</span>}</td>
                  <td>{fmtDateTime(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
